// src/components/permissions/PermissionMatrix.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Tooltip,
  alpha,
  Switch,
  FormControlLabel,
  Skeleton,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  InfoOutlined, 
  LockOpen, 
  Lock,
  MoreVert as MoreIcon,
  AllInclusive as AllIcon,
  PersonOutlined as OwnIcon,
  GroupOutlined as TeamIcon,
  BusinessOutlined as OrganizationIcon,
  AssignmentOutlined as AssignedIcon,
  FolderOutlined as ProjectIcon,
  BrandingWatermarkOutlined as BrandIcon,
  PersonPinOutlined as ClientIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import permissionsService from '../../services/permissionService';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  // borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: theme.shadows[1],
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  position: 'relative',
  
  // Add subtle gradient background
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${theme.palette.background.paper} 100%)`,
    zIndex: -1,
  },
}));

const ResourceCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  textAlign: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
}));

const ActionCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1),
  position: 'relative',
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const ResourceChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
}));

const AnimatedCheckbox = styled(Checkbox)(({ theme }) => ({
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const ScopeIndicator = styled(Box)(({ theme, scope }) => {
  // Different colors for different scopes
  const getScopeColor = () => {
    switch (scope) {
      case 'own':
        return theme.palette.info.main;
      case 'assigned':
        return theme.palette.warning.main;
      case 'team':
        return theme.palette.success.main;
      case 'project':
        return theme.palette.secondary.main;
      case 'brand':
        return theme.palette.error.main;
      case 'client':
        return '#9c27b0'; // purple
      case 'organization':
        return '#795548'; // brown
      case 'all':
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: getScopeColor(),
    top: 4,
    right: 4,
    boxShadow: `0 0 4px ${alpha(getScopeColor(), 0.5)}`,
  };
});

// Icons for each scope
const SCOPE_ICONS = {
  own: <OwnIcon fontSize="small" />,
  assigned: <AssignedIcon fontSize="small" />,
  team: <TeamIcon fontSize="small" />,
  project: <ProjectIcon fontSize="small" />,
  brand: <BrandIcon fontSize="small" />,
  client: <ClientIcon fontSize="small" />,
  organization: <OrganizationIcon fontSize="small" />,
  all: <AllIcon fontSize="small" />,
};

// Descriptions for each scope
const SCOPE_DESCRIPTIONS = {
  own: "Access only to resources created by the user",
  assigned: "Access only to resources assigned to the user",
  team: "Access to resources within the user's team",
  project: "Access to resources within specific projects",
  brand: "Access to resources belonging to specific brands",
  client: "Access to resources belonging to specific clients",
  organization: "Access to resources within the organization",
  all: "Access to all resources regardless of ownership",
};

/**
 * Permission Matrix component for selecting role permissions with per-permission scopes
 * 
 * @param {Object} props
 * @param {Object|Array} props.resources - Available resources (object or array)
 * @param {Object|Array} props.actions - Available actions (object or array)
 * @param {Object|Array} props.scopes - Available scopes (object or array)
 * @param {Array} props.selectedPermissions - Currently selected permissions
 * @param {Function} props.onChange - Callback when permissions change
 * @param {boolean} [props.loading=false] - Loading state
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {Object} [props.descriptions] - Optional resource/action descriptions
 */
const PermissionMatrix = ({
  resources = {},
  actions = {},
  scopes = {},
  selectedPermissions = [],
  onChange,
  loading = false,
  disabled = false,
  descriptions = {}
}) => {
  // Convert flat permissions to grouped structure for easier UI handling
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  
  // Scope menu state
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState(null);
  const [activePermission, setActivePermission] = useState(null);
  
  // Convert resources, actions and scopes objects to arrays if needed
  const resourcesArray = useMemo(() => {
    return Array.isArray(resources) 
      ? resources 
      : Object.values(resources);
  }, [resources]);
  
  const actionsArray = useMemo(() => {
    return Array.isArray(actions) 
      ? actions 
      : Object.values(actions);
  }, [actions]);
  
  const scopesArray = useMemo(() => {
    if (!scopes || (typeof scopes === 'object' && Object.keys(scopes).length === 0)) {
      // Default scopes if none provided
      return ['own', 'assigned', 'team', 'organization', 'all'];
    }
    return Array.isArray(scopes) 
      ? scopes 
      : Object.values(scopes);
  }, [scopes]);
  
  // Set up groupedPermissions when props change
  useEffect(() => {
    if (!loading && resourcesArray.length > 0 && actionsArray.length > 0) {
      setGroupedPermissions(
        permissionsService.groupPermissionsByResource(selectedPermissions, resourcesArray, actionsArray)
      );
    }
  }, [selectedPermissions, resourcesArray, actionsArray, loading]);
  
  // Filter resources based on showOnlySelected
  const displayResources = useMemo(() => {
    if (!showOnlySelected) return resourcesArray;
    
    return resourcesArray.filter(resource => {
      const actionPermissions = groupedPermissions[resource] || {};
      return Object.values(actionPermissions).some(perm => perm && perm.enabled);
    });
  }, [resourcesArray, groupedPermissions, showOnlySelected]);
  
  // Handle opening scope menu
  const handleOpenScopeMenu = (event, resource, action) => {
    event.stopPropagation();
    setScopeMenuAnchor(event.currentTarget);
    setActivePermission({ resource, action });
  };
  
  // Handle closing scope menu
  const handleCloseScopeMenu = () => {
    setScopeMenuAnchor(null);
    setActivePermission(null);
  };
  
  // Handle selecting a scope
  const handleSelectScope = (scope) => {
    if (!activePermission) return;
    
    const { resource, action } = activePermission;
    
    // Update the grouped permissions
    const newGroupedPermissions = {
      ...groupedPermissions,
      [resource]: {
        ...(groupedPermissions[resource] || {}),
        [action]: {
          enabled: true,
          scope
        }
      }
    };
    
    setGroupedPermissions(newGroupedPermissions);
    
    // Convert back to flat structure and call onChange
    const newPermissions = permissionsService.flattenGroupedPermissionsWithScopes(newGroupedPermissions);
    onChange(newPermissions);
    
    // Close the menu
    handleCloseScopeMenu();
  };
  
  // Handle individual permission toggle
  const handlePermissionToggle = (resource, action) => {
    if (disabled) return;
    
    const currentPermission = groupedPermissions[resource]?.[action];
    const isEnabled = currentPermission?.enabled || false;
    
    const newGroupedPermissions = {
      ...groupedPermissions,
      [resource]: {
        ...(groupedPermissions[resource] || {}),
        [action]: {
          enabled: !isEnabled,
          scope: currentPermission?.scope || 'all' // Default to 'all' if no scope is set
        }
      }
    };
    
    setGroupedPermissions(newGroupedPermissions);
    
    // Convert back to flat structure and call onChange
    const newPermissions = permissionsService.flattenGroupedPermissionsWithScopes(newGroupedPermissions);
    onChange(newPermissions);
  };
  
  // Handle row toggle (all permissions for a resource)
  const handleRowToggle = (resource) => {
    if (disabled) return;
    
    const resourcePermissions = groupedPermissions[resource] || {};
    const hasAllPermissions = Object.values(resourcePermissions).every(perm => perm && perm.enabled);
    
    // Toggle all to true if any are false, otherwise toggle all to false
    const newValue = !hasAllPermissions;
    
    const newGroupedPermissions = {
      ...groupedPermissions,
      [resource]: actionsArray.reduce((acc, action) => {
        const currentScope = groupedPermissions[resource]?.[action]?.scope || 'all';
        acc[action] = {
          enabled: newValue,
          scope: currentScope
        };
        return acc;
      }, {})
    };
    
    setGroupedPermissions(newGroupedPermissions);
    
    // Convert back to flat structure and call onChange
    const newPermissions = permissionsService.flattenGroupedPermissionsWithScopes(newGroupedPermissions);
    onChange(newPermissions);
  };
  
  // Handle column toggle (all permissions for an action)
  const handleColumnToggle = (action) => {
    if (disabled) return;
    
    // Check if all resources have this action enabled
    const hasAllPermissions = resourcesArray.every(resource => 
      groupedPermissions[resource]?.[action]?.enabled === true
    );
    
    // Toggle all to true if any are false, otherwise toggle all to false
    const newValue = !hasAllPermissions;
    
    const newGroupedPermissions = { ...groupedPermissions };
    resourcesArray.forEach(resource => {
      newGroupedPermissions[resource] = {
        ...(newGroupedPermissions[resource] || {}),
        [action]: {
          enabled: newValue,
          scope: groupedPermissions[resource]?.[action]?.scope || 'all'
        }
      };
    });
    
    setGroupedPermissions(newGroupedPermissions);
    
    // Convert back to flat structure and call onChange
    const newPermissions = permissionsService.flattenGroupedPermissionsWithScopes(newGroupedPermissions);
    onChange(newPermissions);
  };
  
  // Get the current scope for a permission
  const getPermissionScope = (resource, action) => {
    return groupedPermissions[resource]?.[action]?.scope || 'all';
  };
  
  // Check if a permission is enabled
  const isPermissionEnabled = (resource, action) => {
    return groupedPermissions[resource]?.[action]?.enabled || false;
  };
  
  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Permissions
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="h6">
          Permissions
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={showOnlySelected}
              onChange={() => setShowOnlySelected(!showOnlySelected)}
              disabled={disabled}
            />
          }
          label="Show only selected"
        />
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Click the checkbox to enable/disable a permission. Click the options icon to set the permission scope.
        </Typography>
      </Box>
      
      <StyledTableContainer component={Paper}>
        <Table size="small" aria-label="permissions matrix">
          <TableHead>
            <TableRow>
              <TableCell width="25%">Resource</TableCell>
              {actionsArray.map(action => (
                <HeaderCell key={action} onClick={() => handleColumnToggle(action)}>
                  <Box 
                    sx={{ 
                      cursor: disabled ? 'default' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                      {action}
                    </Typography>
                    {descriptions?.actions?.[action] && (
                      <Tooltip title={descriptions.actions[action]} arrow>
                        <InfoOutlined 
                          fontSize="small" 
                          color="action" 
                          sx={{ fontSize: 16, mt: 0.5 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayResources.map(resource => (
              <TableRow key={resource} hover>
                <ResourceCell 
                  scope="row" 
                  onClick={() => handleRowToggle(resource)}
                  sx={{ cursor: disabled ? 'default' : 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ResourceChip 
                      label={resource} 
                      size="small" 
                      icon={
                        Object.values(groupedPermissions[resource] || {}).some(perm => perm && perm.enabled) 
                          ? <LockOpen fontSize="small" /> 
                          : <Lock fontSize="small" />
                      }
                    />
                    
                    {descriptions?.resources?.[resource] && (
                      <Tooltip title={descriptions.resources[resource]} arrow>
                        <InfoOutlined 
                          fontSize="small" 
                          color="action" 
                          sx={{ ml: 1, fontSize: 16 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </ResourceCell>
                
                {actionsArray.map(action => (
                  <ActionCell 
                    key={`${resource}-${action}`}
                    onClick={() => handlePermissionToggle(resource, action)}
                    sx={{ 
                      bgcolor: isPermissionEnabled(resource, action)
                        ? (theme) => alpha(theme.palette.secondary.main, 0.1)
                        : 'inherit',
                      pr: 0
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AnimatedCheckbox
                        checked={isPermissionEnabled(resource, action)}
                        color="secondary"
                        disabled={disabled}
                        inputProps={{
                          'aria-label': `${action} permission for ${resource}`
                        }}
                      />
                      
                      {isPermissionEnabled(resource, action) && (
                        <>
                          <ScopeIndicator scope={getPermissionScope(resource, action)} />
                          
                          <Tooltip title={`Scope: ${getPermissionScope(resource, action)}`}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleOpenScopeMenu(e, resource, action)}
                              disabled={disabled}
                              sx={{ ml: 0.5, p: 0.5 }}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </ActionCell>
                ))}
              </TableRow>
            ))}
            
            {displayResources.length === 0 && (
              <TableRow>
                <TableCell colSpan={actionsArray.length + 1} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    {showOnlySelected
                      ? 'No permissions selected'
                      : 'No resources available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      {/* Scope selection menu */}
      <Menu
        anchorEl={scopeMenuAnchor}
        open={Boolean(scopeMenuAnchor)}
        onClose={handleCloseScopeMenu}
        PaperProps={{
          sx: { maxHeight: 300 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
          Select Scope
        </Typography>
        
        {scopesArray.map((scope) => (
          <MenuItem 
            key={scope} 
            onClick={() => handleSelectScope(scope)}
            selected={activePermission && 
              getPermissionScope(activePermission.resource, activePermission.action) === scope}
          >
            <ListItemIcon>
              {SCOPE_ICONS[scope] || <InfoOutlined fontSize="small" />}
            </ListItemIcon>
            <ListItemText 
              primary={scope} 
              secondary={SCOPE_DESCRIPTIONS[scope]} 
              primaryTypographyProps={{ sx: { textTransform: 'capitalize' } }}
              secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default PermissionMatrix;